// contentStore.ts
import { create } from 'zustand';
import {
    contentService,
    ContentDocument,
    ContentVersion,
    MediaItem,
    SEOSettings,
    PublishingSettings,
    CreateContentDto,
    UpdateContentDto,
} from '../services/content.service';
import { toast } from 'sonner';

export type { ContentDocument, ContentVersion, MediaItem, SEOSettings, PublishingSettings };

// export interface CollaboratorCursor {
//     id: string;
//     name: string;
//     color: string;
//     position: number;
//     isActive: boolean;
// }

interface ContentStore {
    // State
    currentDocument: ContentDocument | null;
    isLoading: boolean;
    isSaving: boolean;
    lastSaved: Date | null;
    autoSaveEnabled: boolean;
    // collaborators: CollaboratorCursor[];
    mediaLibrary: MediaItem[];
    recentDocuments: ContentDocument[];

    // Editor state
    editorContent: string;
    wordCount: number;
    readingTime: number;
    currentVersion: string;
    hasUnsavedChanges: boolean;

    // UI state
    showPreview: boolean;
    showVersionHistory: boolean;
    showSEOPanel: boolean;
    showMediaLibrary: boolean;
    sidebarCollapsed: boolean;

    // Actions
    createNewDocument: (document: CreateContentDto) => Promise<void>;
    loadDocument: (id: string) => Promise<void>;
    saveDocument: () => Promise<void>;
    autoSave: () => Promise<void>;
    updateContent: (content: string) => void;
    updateTitle: (title: string) => void;
    updateSEOSettings: (settings: Partial<SEOSettings>) => void;
    getAllDocuments: () => Promise<void>;


    // Version control
    saveNewVersion: (comment: string) => Promise<void>;
    getVersions: () => Promise<void>;
    restoreVersion: (versionId: string) => Promise<void>;

    // Media management
    uploadMedia: (file: File) => Promise<MediaItem>;
    deleteMedia: (id: string) => void;
    insertMedia: (mediaId: string, position: number) => void;


    // Export
    exportDocument: (format: 'html' | 'markdown' | 'pdf') => Promise<void>;



    // UI toggles
    togglePreview: () => void;
    toggleVersionHistory: () => void;
    toggleSEOPanel: () => void;
    toggleMediaLibrary: () => void;
    toggleSidebar: () => void;

    // Utility
    calculateReadingTime: (content: string) => number;
    generateSlug: (title: string) => string;
    resetEditor: () => void;
}

const useContentStore = create<ContentStore>((set, get) => ({
    // Initial state
    currentDocument: null,
    isLoading: false,
    isSaving: false,
    lastSaved: null,
    autoSaveEnabled: true,
    collaborators: [],
    mediaLibrary: [],
    recentDocuments: [],

    // Editor state
    editorContent: '',
    wordCount: 0,
    readingTime: 0,
    currentVersion: 'v1.0',
    hasUnsavedChanges: false,

    // UI state
    showPreview: false,
    showVersionHistory: false,
    showSEOPanel: false,
    showMediaLibrary: false,
    sidebarCollapsed: false,

    // Actions
    createNewDocument: async (document: CreateContentDto) => {
        set({ isSaving: true });
        try {
            const newDoc = await contentService.createDocument(document);
            set({
                currentDocument: newDoc,
                editorContent: newDoc.content,
                wordCount: newDoc.wordCount,
                readingTime: newDoc.readingTime,
                hasUnsavedChanges: false,
                isSaving: false,
                lastSaved: new Date(),
            });
            toast.success('New document created successfully!');
        } catch (error) {
            set({ isSaving: false });
            console.error('Create new document failed:', error);
            toast.error('Failed to create new document.');
        }
    },

    loadDocument: async (id: string) => {
        set({ isLoading: true });
        try {
            const doc = await contentService.getDocument(id);
            set({
                currentDocument: doc,
                editorContent: doc.content,
                wordCount: doc.wordCount,
                readingTime: doc.readingTime,
                hasUnsavedChanges: false,
                isLoading: false,
            });
            // toast.success('Document loaded successfully!');
        } catch (error) {
            set({ isLoading: false });
            console.error('Failed to load document:', error);
            // toast.error('Failed to load document.');
        }
    },
    getAllDocuments: async () => {
        set({ isLoading: true });
        try {
            const documents = await contentService.listDocuments();
            set({
                recentDocuments: documents,
                isLoading: false,
            });
            // toast.success('All documents loaded successfully!');
        } catch (error) {
            set({ isLoading: false });
            console.error('Failed to load all documents:', error);
            // toast.error('Failed to load documents. Please try again.');
        }
    },

    saveDocument: async () => {
        const { currentDocument, editorContent, hasUnsavedChanges } = get();
        if (!currentDocument || !hasUnsavedChanges) return;

        set({ isSaving: true });
        try {
            const updates: UpdateContentDto = {
                content: editorContent,
                title: currentDocument.title,
                tags: currentDocument.tags,
                seoSettings: currentDocument.seoSettings,
            };
            const updatedDoc = await contentService.updateDocument(currentDocument._id, updates);
            set({
                currentDocument: updatedDoc,
                hasUnsavedChanges: false,
                isSaving: false,
                lastSaved: new Date(),
            });
            toast.success('Document saved!');
        } catch (error) {
            set({ isSaving: false });
            console.error('Failed to save document:', error);
            toast.error('Failed to save document.');
        }
    },

    autoSave: async () => {
        const { autoSaveEnabled, hasUnsavedChanges, isSaving, currentDocument } = get();
        if (!autoSaveEnabled || !hasUnsavedChanges || isSaving || !currentDocument) return;

        try {
            const updates: UpdateContentDto = {
                content: get().editorContent,
            };
            await contentService.updateDocument(currentDocument._id, updates);
            set({
                hasUnsavedChanges: false,
                lastSaved: new Date(),
            });
        } catch (error) {
            console.error('Auto-save failed:', error);
        }
    },

    updateContent: (content: string) => {
        const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;
        const readingTime = get().calculateReadingTime(content);

        set({
            editorContent: content,
            wordCount,
            readingTime,
            hasUnsavedChanges: true,
        });
    },

    updateTitle: (title: string) => {
        const { currentDocument } = get();
        if (!currentDocument) return;

        set({
            currentDocument: {
                ...currentDocument,
                title,
            },
            hasUnsavedChanges: true,
        });
    },

    updateSEOSettings: (settings: Partial<SEOSettings>) => {
        const { currentDocument } = get();
        if (!currentDocument) return;

        const newSeoSettings = { ...currentDocument.seoSettings, ...settings };
        set({
            currentDocument: {
                ...currentDocument,
                seoSettings: newSeoSettings,
            },
            hasUnsavedChanges: true,
        });
    },


    saveNewVersion: async (comment: string) => {
        const { currentDocument, editorContent } = get();
        if (!currentDocument) return;

        try {
            const newVersion = await contentService.saveVersion(currentDocument._id, {
                content: editorContent,
                autoSave: false,
                comment: comment,
            });

            set(state => ({
                currentDocument: state.currentDocument
                    ? {
                        ...state.currentDocument,
                        versions: [newVersion, ...state.currentDocument.versions],
                    }
                    : null,
                currentVersion: newVersion.version,
            }));

            toast.success('New version saved!');
        } catch (error) {
            console.error('Failed to save new version:', error);
            toast.error('Failed to save new version.');
        }
    },

    getVersions: async () => {
        const { currentDocument } = get();
        if (!currentDocument) return;

        set({ isLoading: true });
        try {
            const versions = await contentService.getVersions(currentDocument._id);
            set(state => ({
                currentDocument: state.currentDocument ? {
                    ...state.currentDocument,
                    versions,
                } : null,
                isLoading: false,
            }));
        } catch (error) {
            set({ isLoading: false });
            console.error('Failed to fetch versions:', error);
            // toast.error('Failed to load versions.');
        }
    },

    restoreVersion: async (versionId: string) => {
        const { currentDocument } = get();
        if (!currentDocument) return;

        try {
            const restoredDoc = await contentService.restoreVersion(currentDocument._id, versionId);
            set({
                currentDocument: restoredDoc,
                editorContent: restoredDoc.content,
                hasUnsavedChanges: true,
            });
            toast.success('Version restored successfully!');
        } catch (error) {
            console.error('Failed to restore version:', error);
            toast.error('Failed to restore version.');
        }
    },

    uploadMedia: async (file: File): Promise<MediaItem> => {
        // This is a placeholder as no API is provided for media upload.
        console.log('Uploading media...', file);
        return {
            id: 'mock_media_id',
            type: file.type.startsWith('image/') ? 'image' : 'document',
            name: file.name,
            url: URL.createObjectURL(file),
            size: file.size,
            uploadedAt: new Date(),
        };
    },

    deleteMedia: (id: string) => {
        // This is a placeholder as no API is provided for media deletion.
        console.log('Deleting media with ID:', id);
        set(state => ({
            mediaLibrary: state.mediaLibrary.filter(item => item.id !== id),
        }));
    },

    insertMedia: (mediaId: string, position: number) => {
        const { mediaLibrary, editorContent } = get();
        const media = mediaLibrary.find(item => item.id === mediaId);

        if (media) {
            const mediaMarkdown = media.type === 'image'
                ? `![${media.name}](${media.url})`
                : `[${media.name}](${media.url})`;

            const newContent = editorContent.slice(0, position) + mediaMarkdown + editorContent.slice(position);

            get().updateContent(newContent);
        }
    },



    exportDocument: async (format: 'html' | 'markdown' | 'pdf') => {
        const { currentDocument } = get();
        if (!currentDocument) {
            toast.error('No document to export.');
            return;
        }

        try {
            const blob = await contentService.exportDocument(currentDocument._id, { format });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${currentDocument.title}.${format}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success('Document exported successfully!');
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Failed to export document.');
        }
    },


    // UI toggles
    togglePreview: () => set(state => ({ showPreview: !state.showPreview })),
    toggleVersionHistory: () => set(state => ({ showVersionHistory: !state.showVersionHistory })),
    toggleSEOPanel: () => set(state => ({ showSEOPanel: !state.showSEOPanel })),
    toggleMediaLibrary: () => set(state => ({ showMediaLibrary: !state.showMediaLibrary })),
    toggleSidebar: () => set(state => ({ sidebarCollapsed: !state.sidebarCollapsed })),

    // Utility functions
    calculateReadingTime: (content: string) => {
        const wordsPerMinute = 200;
        const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;
        return Math.ceil(wordCount / wordsPerMinute);
    },

    generateSlug: (title: string) => {
        return title
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .trim();
    },

    resetEditor: () => {
        set({
            currentDocument: null,
            editorContent: '',
            wordCount: 0,
            readingTime: 0,
            hasUnsavedChanges: false,
            showPreview: false,
            showVersionHistory: false,
            showSEOPanel: false,
            showMediaLibrary: false,
        });
    },
}));

// Auto-save interval
if (typeof window !== 'undefined') {
    setInterval(() => {
        const store = useContentStore.getState();
        store.autoSave();
    }, 30000);
}

export default useContentStore;